import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Stack,
  Avatar
} from '@mui/material';
import {
  DisplaySettings,
  ContentCopy,
  CalendarToday,
  Settings,
  People,
  TrendingUp,
  EmojiEvents,
  Speed,
  Palette
} from '@mui/icons-material';
import Navigation from './Navigation';
import { homepageContent } from '../content/pages/homepage';

// Icon mapping helper
const IconMap: Record<string, React.ReactElement> = {
  CalendarToday: <CalendarToday />,
  People: <People />,
  Palette: <Palette />,
  DisplaySettings: <DisplaySettings />,
  ContentCopy: <ContentCopy />,
  Settings: <Settings />,
  TrendingUp: <TrendingUp />,
  Speed: <Speed />,
  EmojiEvents: <EmojiEvents />
};

const Homepage: React.FC = () => {
  const c = homepageContent;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />
        
        {/* Navigation */}
        <Navigation />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ mt: 4 }}>
            {/* Hero Content - Split horizontally */}
            <Grid container spacing={4} alignItems="center">
              {/* Logo Section - Left Side */}
              <Grid item xs={12} md={5}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <img
                      src="/images/familycast_logo_1000x1000.png"
                      alt={c.hero.logoAlt}
                      style={{
                        width: 'auto',
                        maxWidth: '400px',
                        filter: 'brightness(0) invert(1)',
                        opacity: 1
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              
              {/* Text Content - Right Side */}
              <Grid item xs={12} md={7}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h4" component="h2" sx={{ opacity: 0.9, maxWidth: 600, lineHeight: 1.4, mb: 3, fontWeight: 600 }}>
                    {c.hero.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 600 }}>
                    {c.hero.subtitle}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="large" 
                    component={Link}
                    to="/beta"
                    sx={{ 
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'white', 
                      borderColor: 'white', 
                      borderWidth: 2,
                      '&:hover': { 
                        borderColor: 'white', 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderWidth: 2
                      }
                    }}
                  >
                    {c.hero.cta}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Problem Statement Section - Empathetic Content */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4, color: '#2D2C2A' }}>
              {c.problem.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6, color: '#5A5856' }}>
              {c.problem.subtitle}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {c.problem.paragraphs.map((para, i) => (
              <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3, color: '#2D2C2A' }}>
                {para}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Beta Program Promo Section - More Prominent */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <EmojiEvents sx={{ fontSize: 80, color: '#ff9800', mb: 3 }} />
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3, color: '#2D2C2A' }}>
              {c.beta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6, color: '#5A5856' }}>
              {c.beta.subtitle}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 700, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6, color: '#2D2C2A' }}>
              {c.beta.description}
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              component={Link} 
              to="/beta"
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.2rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #ff9800 0%, #b26a00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ffad33 0%, #ff9800 100%)',
                }
              }}
            >
              {c.beta.cta}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Value Propositions Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            {c.whatWeDo.title}
          </Typography>
          <Typography variant="h6" sx={{ color: "#5A5856", maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {c.whatWeDo.subtitle}
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {c.valuePropositions.map((proposition, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, boxShadow: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 3, width: 64, height: 64 }}>
                  {IconMap[proposition.iconName]}
                </Avatar>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  {proposition.title}
                </Typography>
                <Typography variant="h6" component="h4" gutterBottom sx={{ color: "#5A5856", mb: 3 }}>
                  {proposition.subtitle}
                </Typography>
                <Typography variant="body1" sx={{ color: "#5A5856", mb: 4, fontSize: '1rem', lineHeight: 1.6 }}>
                  {proposition.description}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  {proposition.benefits.map((benefit, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Box sx={{
                        color: '#4caf50', 
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {/* Dynamic Icon selection based on content keywords - simple fallback for now */}
                        {benefit.includes('Automated') && <TrendingUp sx={{ fontSize: 20 }} />}
                        {benefit.includes('Centralized') && <Settings sx={{ fontSize: 20 }} />}
                        {benefit.includes('Time-Saving') && <Speed sx={{ fontSize: 20 }} />}
                        {benefit.includes('Reduced') && <TrendingUp sx={{ fontSize: 20 }} />}
                        {benefit.includes('Dynamic') && <ContentCopy sx={{ fontSize: 20 }} />}
                        {benefit.includes('Real-Time') && <Speed sx={{ fontSize: 20 }} />}
                        {benefit.includes('Engaging') && <ContentCopy sx={{ fontSize: 20 }} />}
                        {benefit.includes('Better') && <TrendingUp sx={{ fontSize: 20 }} />}
                        {benefit.includes('Easy-to-Use') && <Settings sx={{ fontSize: 20 }} />}
                        {benefit.includes('Team') && <People sx={{ fontSize: 20 }} />}
                        {benefit.includes('Communication') && <ContentCopy sx={{ fontSize: 20 }} />}
                        {benefit.includes('Streamlined') && <Speed sx={{ fontSize: 20 }} />}
                        
                        {/* Fallback if no match above */}
                        {!benefit.match(/(Automated|Centralized|Time-Saving|Reduced|Dynamic|Real-Time|Engaging|Better|Easy-to-Use|Team|Communication|Streamlined)/) && 
                         <ContentCopy sx={{ fontSize: 20 }} />}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#5A5856', lineHeight: 1.4 }}>
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Content Types Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.commandCenter.title}
            </Typography>
            <Typography variant="h6" sx={{ color: "#5A5856", maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6, mb: 4 }}>
              {c.commandCenter.subtitle}
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            {c.commandCenter.paragraphs.map((para, i) => (
              <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 3, color: '#2D2C2A' }}>
                {para}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Content Integration Section - NEW */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.integration.title}
            </Typography>
            <Typography variant="h6" sx={{ color: "#5A5856", maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {c.integration.subtitle}
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {c.integration.features.map((feature, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3, boxShadow: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 3, width: 64, height: 64 }}>
                    {React.cloneElement(IconMap[feature.iconName], { sx: { fontSize: 32, color: 'white' } })}
                  </Avatar>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#5A5856", mb: 4, fontSize: '1rem', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{ color: '#4caf50', mr: 2, display: 'flex', alignItems: 'center' }}>
                          <TrendingUp sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#5A5856', lineHeight: 1.4 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#4caf50', color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.finalCta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 6, opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 700, mx: 'auto' }}>
              {c.finalCta.subtitle}
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
              <Button 
                variant="outlined" 
                size="large" 
                component={Link}
                to="/beta"
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'white', 
                  borderColor: 'white', 
                  borderWidth: 2,
                  '&:hover': { 
                    borderColor: 'white', 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderWidth: 2
                  }
                }}
              >
                {c.finalCta.cta}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {c.footer.copyright}
            </Typography>
            <Typography 
              variant="body2" 
              component="button"
              onClick={() => window.location.href = '/cookie-preferences'}
              sx={{ 
                opacity: 0.7, 
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                color: 'white',
                textDecoration: 'underline',
                '&:hover': { opacity: 1 }
              }}
            >
              {c.footer.cookiePreferences}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Homepage; 